<template>
  <ValidationProvider
    :class="['form__control select__wrapper', className]"
    :vid="vid"
    :rules="rules"
    :name="name"
    :custom-messages="customMessages"
    tag="div"
    ref="provider"
    v-slot="{ errors, invalid }"
  >
    <label :for="vid">
      <span :class="['label', labelClass]">{{ label }}</span>
    </label>

    <v-select
      :id="vid"
      :error-messages="errors[0] || externalErrorMsg"
      :items="formatListItems"
      :label="selectedDefault"
      :class="['input__control', { 'field-invalid': invalid }]"
      background-color="white"
      append-icon="mdi-chevron-down"
      :disabled="isDisabled"
      v-model="innerValue"
      :aria-label="ariaLabel"
      single-line
      outlined
      ref="input"
      solo
      flat
      :hide-details="hideDetails"
      :dense="dense"
      :data-uiid="dataUiid"
      :height="height"
      :return-object="returnObject"
      :menu-props="menuProps"
      @change="onInput"
    >
      <template v-slot:message="{ message }">
        <span v-html="message"></span>
      </template>

      <template v-slot:selection="{ item }">
        <div class="v-select__selection v-select__selection--comma">
          {{ item.text }}
        </div>
      </template>

      <template v-slot:item="{ item, on, attrs }">
        <v-list-item v-on="on" v-bind="attrs" :data-uiid="item.dataUiid">
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template v-slot:append>
        <span>
          <v-icon
            v-if="errors[0] || externalErrorMsg"
            size="20"
            class="error--text"
            :class="[iconStyleClass]"
            >mdi-alert-circle</v-icon
          >
          <v-icon class="black--text">mdi-chevron-down</v-icon>
        </span>
      </template>
    </v-select>
  </ValidationProvider>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import { capitalizeLetterText } from "@/common/utils";
import { generateAlphaSort } from "@/common/sorting";

export default {
  name: "BaseSelect",
  components: {
    ValidationProvider,
  },
  props: {
    hideDetails: {
      type: [Boolean, String],
      default: "auto",
    },
    vid: String,
    value: null,
    className: String,
    name: String,
    fieldName: String,
    label: String,
    labelClass: String,
    ariaLabel: String,
    externalErrorMsg: String,
    customMessages: Object,
    selectedDefault: {
      type: String,
      default: "Please select...",
    },
    rules: [Object, String],
    items: [],
    isDisabled: Boolean,
    dense: {
      type: Boolean,
      default: true,
    },
    dataUiid: {
      type: String,
      default: "",
    },
    height: [String, Number],
    iconStyleClass: {
      type: String,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    isEnableCapitalizeLetterListItems: {
      type: Boolean,
      default: true,
    },
    isEnableSortOrderListItems: {
      type: Boolean,
      default: true,
    },
    menuProps: {
      type: Object,
      default: () => {
        return {
          bottom: true,
          offsetY: true,
          allowOverflow: true,
        };
      },
    },
  },
  data: () => ({
    innerValue: "",
  }),
  watch: {
    // Handles internal model changes.
    innerValue(value) {
      this.$emit("input", value, this.fieldName);
    },
    // Handles external model changes.
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val;
      }
    },
  },
  computed: {
    formatListItems() {
      if (!this.items) return [];
      // format item display text
      if (this.isEnableCapitalizeLetterListItems) {
        this.items.map((item) => {
          item.text = capitalizeLetterText(item.text);
        });
      }
      // Sort a clone of the array
      if (this.isEnableSortOrderListItems) {
        return this.items.map((x) => x).sort(generateAlphaSort("text"));
      }
      return this.items;
    },
  },
  created() {
    if (this.value) {
      this.innerValue = this.value;
    }
  },
  methods: {
    onInput(val) {
      this.$emit("change", val);
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-select__slot) {
  .v-label,
  div {
    color: $black !important;
  }
}
</style>
